import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Truck, Building2, Warehouse, PackagePlus, Ban, RefreshCw,
    MapPin, Calendar, User, Plus, Pencil, Trash2, ChevronDown, ChevronRight, X,
    ClipboardList, CircleAlert,
} from "lucide-react";

import { api } from "@/lib/api";

/**
 * DEV-008 · Delivery & Site Logistics — Operations-only.
 *
 * Renders inside `OperationsProjectDetail`. Handles list, create, edit
 * (inline status quick-change + full edit dialog), and delete with an
 * inline confirmation. All lifecycle changes hit `/api/logistics` and
 * emit Activity Engine events server-side (see catalog `logistics.*`).
 */

const TYPE_META = {
    equipment_delivery:    { label: "Equipment delivery",    icon: PackagePlus,  colour: "#0EA5E9" },
    scaffolding_erection:  { label: "Scaffolding erection",  icon: Building2,    colour: "#F59E0B" },
    scaffolding_dismantle: { label: "Scaffolding dismantle", icon: Building2,    colour: "#EF4444" },
    skip_delivery:         { label: "Skip delivery",         icon: Truck,        colour: "#8B5CF6" },
    skip_collection:       { label: "Skip collection",       icon: Truck,        colour: "#6366F1" },
    other:                 { label: "Other",                 icon: ClipboardList,colour: "#4B5565" },
};

const DEST_META = {
    customer_site:    { label: "Customer site",     icon: MapPin,     tone: "customer" },
    pearl_warehouse:  { label: "Pearl warehouse",   icon: Warehouse,  tone: "internal" },
    other:            { label: "Other",             icon: MapPin,     tone: "internal" },
};

const STATUS_META = {
    planned:   { label: "Planned",   dot: "#3B82F6", bg: "#EFF6FF", fg: "#1D4ED8" },
    confirmed: { label: "Confirmed", dot: "#3E9E2E", bg: "#F1F8EC", fg: "#166534" },
    delayed:   { label: "Delayed",   dot: "#F59E0B", bg: "#FEF3C7", fg: "#B45309" },
    completed: { label: "Completed", dot: "#4B5565", bg: "#F1F5F9", fg: "#334155" },
    cancelled: { label: "Cancelled", dot: "#EF4444", bg: "#FEF2F2", fg: "#B91C1C" },
};

const STATUS_ORDER = ["planned", "confirmed", "delayed", "completed", "cancelled"];

function fmtDate(iso) {
    if (!iso) return "—";
    const d = new Date(iso.length <= 10 ? `${iso}T00:00:00Z` : iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

// ─── Section wrapper ─────────────────────────────────────────────────────

export default function LogisticsSection({ project, onProjectChanged }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialog, setDialog] = useState(null);   // null | "create" | logistics-object
    const [deleting, setDeleting] = useState(null);
    // OPS-PRJ-001 · Collapsible section. Defaults collapsed on mount and
    // resets to collapsed whenever the project changes (`project.id`).
    // Add/Edit dialogs preserve the previous expanded/collapsed state
    // because their open/close does not touch `expanded`.
    const [expanded, setExpanded] = useState(false);
    const toggleBtnRef = useRef(null);
    useEffect(() => { setExpanded(false); }, [project.id]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await api.listLogistics(project.id);
            setItems(Array.isArray(data) ? data : []);
        } catch {
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, [project.id]);

    useEffect(() => { load(); }, [load]);

    // WF-001A2B · After a successful logistics mutation the backend may
    // have auto-completed / reconciled a canonical task via WF-001A2A
    // (e.g. `logistics.scaffolding.erect`, `procurement.materials_delivered`).
    // Route through the shared canonical refresh so the project's task
    // rows, phase counts and outstanding total pick up the sync in the
    // same rendered view — no browser reload / no polling.
    const refreshProject = useCallback(() => {
        if (typeof onProjectChanged === "function") {
            return Promise.resolve(onProjectChanged());
        }
        return Promise.resolve();
    }, [onProjectChanged]);

    const onCreated = async () => { setDialog(null); await load(); await refreshProject(); };
    const onUpdated = async () => { setDialog(null); await load(); await refreshProject(); };
    const onDeleted = async (id) => {
        try {
            await api.deleteLogistics(id);
            setDeleting(null);
            await load();
            await refreshProject();
        } catch (e) {
            setDeleting({ id, error: e?.response?.data?.detail || "Delete failed" });
        }
    };

    const quickStatus = async (row, next) => {
        try {
            await api.updateLogistics(row.id, { status: next });
            await load();
            await refreshProject();
        } catch (e) { console.error("logistics status update failed", e); }
    };

    const customerFacingCount = useMemo(
        () => items.filter((i) => i.customer_facing && i.status !== "cancelled").length,
        [items]
    );

    return (
        <section
            id="logistics"
            data-testid="ops-projects-detail-logistics"
            className="rounded-xl border border-[#E5E7EB] bg-white"
        >
            <header className="px-5 py-4 flex items-center gap-3 border-b border-[#E5E7EB]">
                <span className="w-8 h-8 rounded-lg bg-[#F1F5F9] text-[#131B2F] flex items-center justify-center">
                    <Truck size={16} strokeWidth={1.75} />
                </span>
                {/* OPS-PRJ-001 · The dedicated toggle occupies the header
                    text/summary region only. Add and per-row controls
                    live OUTSIDE this button so they never toggle the
                    section. Keyboard activation works via native
                    <button> semantics. */}
                <button
                    type="button"
                    ref={toggleBtnRef}
                    onClick={() => setExpanded((v) => !v)}
                    aria-expanded={expanded}
                    aria-controls="ops-projects-detail-logistics-body"
                    aria-label={expanded
                        ? "Collapse delivery & site logistics"
                        : "Expand delivery & site logistics"}
                    data-testid="ops-projects-detail-logistics-toggle"
                    className="flex-1 flex items-center gap-2 text-left rounded-md -mx-1 px-1 py-1 hover:bg-[#F5F5F7] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#131B2F]/40"
                >
                    <div className="flex-1">
                        <div className="text-sm font-semibold text-[#131B2F]">Delivery & site logistics</div>
                        <div
                            className="text-xs text-[#4B5565] mt-0.5"
                            data-testid="ops-projects-detail-logistics-summary"
                        >
                            {loading ? "Loading…" :
                                items.length === 0 ? "No logistics scheduled" :
                                `${items.length} item${items.length===1?"":"s"}` +
                                (customerFacingCount ? ` · ${customerFacingCount} affect${customerFacingCount===1?"s":""} the customer site` : "")}
                        </div>
                    </div>
                    {expanded
                        ? <ChevronDown size={16} strokeWidth={1.75} data-testid="ops-projects-detail-logistics-chevron-down" />
                        : <ChevronRight size={16} strokeWidth={1.75} data-testid="ops-projects-detail-logistics-chevron-right" />}
                </button>
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setDialog("create"); }}
                    data-testid="logistics-add"
                    className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full text-sm font-medium bg-[#131B2F] text-white hover:brightness-110 transition-all"
                >
                    <Plus size={14} strokeWidth={2.25} /> Add
                </button>
            </header>

            {expanded && (
              <div id="ops-projects-detail-logistics-body" data-testid="ops-projects-detail-logistics-body">
                {loading ? (
                    <div className="p-6 text-sm text-[#4B5565]" data-testid="logistics-loading">Loading logistics…</div>
                ) : items.length === 0 ? (
                    <div className="p-8 text-center" data-testid="logistics-empty">
                        <div className="text-sm text-[#131B2F] font-medium">No logistics items yet</div>
                        <div className="text-xs text-[#4B5565] mt-1">Track supplier deliveries, scaffolding, skips and other site activity.</div>
                    </div>
                ) : (
                    <ul className="divide-y divide-[#F1F5F9]" data-testid="logistics-list">
                        {items.map((row) => (
                            <LogisticsRow
                                key={row.id}
                                row={row}
                                onEdit={() => setDialog(row)}
                                onQuickStatus={(next) => quickStatus(row, next)}
                                onDeleteClick={() => setDeleting({ id: row.id })}
                            />
                        ))}
                    </ul>
                )}
              </div>
            )}

            {dialog === "create" && (
                <LogisticsDialog
                    mode="create"
                    projectId={project.id}
                    onClose={() => setDialog(null)}
                    onSaved={onCreated}
                />
            )}
            {dialog && dialog !== "create" && (
                <LogisticsDialog
                    mode="edit"
                    projectId={project.id}
                    initial={dialog}
                    onClose={() => setDialog(null)}
                    onSaved={onUpdated}
                />
            )}
            {deleting && (
                <ConfirmDelete
                    error={deleting.error}
                    onCancel={() => setDeleting(null)}
                    onConfirm={() => onDeleted(deleting.id)}
                />
            )}
        </section>
    );
}


// ─── Row ─────────────────────────────────────────────────────────────────

function LogisticsRow({ row, onEdit, onQuickStatus, onDeleteClick }) {
    const type = TYPE_META[row.logistics_type] || TYPE_META.other;
    const TypeIcon = type.icon;
    const dest = DEST_META[row.destination] || DEST_META.other;
    const DestIcon = dest.icon;
    const status = STATUS_META[row.status] || STATUS_META.planned;
    const [statusOpen, setStatusOpen] = useState(false);

    useEffect(() => {
        if (!statusOpen) return;
        const onKey = (e) => e.key === "Escape" && setStatusOpen(false);
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [statusOpen]);

    return (
        <li
            className="px-5 py-4 flex items-start gap-4 group"
            data-testid={`logistics-row-${row.id}`}
        >
            <span
                className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center"
                style={{ background: `${type.colour}18`, color: type.colour }}
            >
                <TypeIcon size={16} strokeWidth={1.75} />
            </span>

            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-[#131B2F]">{type.label}</span>
                    {row.customer_facing && (
                        <span
                            data-testid="logistics-customer-facing-pill"
                            className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-[#F1F8EC] text-[#166534] inline-flex items-center gap-1"
                        >
                            <CircleAlert size={10} strokeWidth={2.25} /> Customer site
                        </span>
                    )}
                    {row.supplier && (
                        <span className="text-xs text-[#4B5565] inline-flex items-center gap-1">
                            <User size={11} strokeWidth={1.75} /> {row.supplier}
                        </span>
                    )}
                </div>
                {row.description && (
                    <div className="text-sm text-[#4B5565] mt-1">{row.description}</div>
                )}
                <div className="flex items-center gap-4 mt-2 text-xs text-[#4B5565] flex-wrap">
                    <span className="inline-flex items-center gap-1" data-testid={`logistics-date-${row.id}`}>
                        <Calendar size={12} strokeWidth={1.75} />
                        {fmtDate(row.expected_date)}{row.expected_window ? ` · ${row.expected_window}` : ""}
                    </span>
                    <span className="inline-flex items-center gap-1">
                        <DestIcon size={12} strokeWidth={1.75} />
                        {dest.label}{row.destination === "other" && row.destination_details ? ` — ${row.destination_details}` : ""}
                    </span>
                </div>
                {row.notes && (
                    <div className="text-xs text-[#4B5565] italic mt-2">{row.notes}</div>
                )}
            </div>

            <div className="shrink-0 flex items-start gap-2">
                {/* Status pill + quick-change dropdown */}
                <div className="relative">
                    <button
                        type="button"
                        onClick={() => setStatusOpen((v) => !v)}
                        data-testid={`logistics-status-${row.id}`}
                        className="inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-full"
                        style={{ background: status.bg, color: status.fg }}
                    >
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: status.dot }} />
                        {status.label}
                        <ChevronDown size={11} strokeWidth={2.25} />
                    </button>
                    {statusOpen && (
                        <div
                            className="absolute right-0 mt-1 w-40 bg-white border border-[#E5E7EB] rounded-lg shadow-lg z-10 overflow-hidden"
                            data-testid={`logistics-status-menu-${row.id}`}
                        >
                            {STATUS_ORDER.map((s) => (
                                <button
                                    key={s}
                                    type="button"
                                    onClick={() => { setStatusOpen(false); if (s !== row.status) onQuickStatus(s); }}
                                    data-testid={`logistics-status-option-${s}`}
                                    className="w-full text-left px-3 py-2 text-xs hover:bg-[#F5F5F7] flex items-center gap-2"
                                >
                                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: STATUS_META[s].dot }} />
                                    {STATUS_META[s].label}
                                    {row.status === s && <span className="ml-auto text-[#131B2F]">•</span>}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                <button
                    type="button"
                    onClick={onEdit}
                    data-testid={`logistics-edit-${row.id}`}
                    className="w-7 h-7 rounded-md flex items-center justify-center text-[#4B5565] hover:bg-[#F1F5F9] hover:text-[#131B2F]"
                    title="Edit"
                >
                    <Pencil size={13} strokeWidth={1.75} />
                </button>
                <button
                    type="button"
                    onClick={onDeleteClick}
                    data-testid={`logistics-delete-${row.id}`}
                    className="w-7 h-7 rounded-md flex items-center justify-center text-[#4B5565] hover:bg-[#FEF2F2] hover:text-[#B91C1C]"
                    title="Remove"
                >
                    <Trash2 size={13} strokeWidth={1.75} />
                </button>
            </div>
        </li>
    );
}


// ─── Dialog ──────────────────────────────────────────────────────────────

function LogisticsDialog({ mode, projectId, initial, onClose, onSaved }) {
    const isEdit = mode === "edit";
    const [form, setForm] = useState(() => ({
        logistics_type:     initial?.logistics_type     || "equipment_delivery",
        supplier:           initial?.supplier           || "",
        description:        initial?.description        || "",
        expected_date:      (initial?.expected_date || "").slice(0, 10),
        expected_window:    initial?.expected_window    || "",
        destination:        initial?.destination        || "customer_site",
        destination_details:initial?.destination_details|| "",
        customer_facing:    initial?.customer_facing    ?? (initial?.destination === "customer_site"),
        status:             initial?.status             || "planned",
        notes:              initial?.notes              || "",
    }));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const set = (k) => (e) => {
        const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        setForm((f) => {
            const next = { ...f, [k]: val };
            // Auto-flip customer_facing to match destination unless the user
            // has explicitly toggled it away — light UX affordance only.
            if (k === "destination" && !isEdit) {
                next.customer_facing = val === "customer_site";
            }
            return next;
        });
    };

    const submit = async (e) => {
        e.preventDefault();
        setError(null);
        if (!form.expected_date) { setError("Expected date is required"); return; }
        setSaving(true);
        try {
            const body = {
                ...form,
                expected_window: form.expected_window || null,
                destination_details: form.destination === "other" ? form.destination_details : null,
            };
            if (isEdit) await api.updateLogistics(initial.id, body);
            else await api.createLogistics(projectId, body);
            onSaved();
        } catch (err) {
            setError(err?.response?.data?.detail || err?.message || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
            data-testid="logistics-dialog"
            role="dialog"
            aria-modal="true"
        >
            <form
                onSubmit={submit}
                className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[92vh] overflow-y-auto"
            >
                <header className="px-5 py-4 border-b border-[#E5E7EB] flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[#F1F5F9] text-[#131B2F] flex items-center justify-center">
                        <Truck size={16} strokeWidth={1.75} />
                    </div>
                    <div className="flex-1">
                        <div className="text-sm font-semibold text-[#131B2F]">
                            {isEdit ? "Edit logistics item" : "New logistics item"}
                        </div>
                        <div className="text-xs text-[#4B5565]">Track a delivery or third-party site activity.</div>
                    </div>
                    <button type="button" onClick={onClose} className="p-1.5 text-[#4B5565] hover:text-[#131B2F]" data-testid="logistics-dialog-close">
                        <X size={16} strokeWidth={1.75} />
                    </button>
                </header>

                <div className="p-5 space-y-4">
                    <Field label="Type">
                        <Select value={form.logistics_type} onChange={set("logistics_type")} testid="logistics-field-type">
                            {Object.entries(TYPE_META).map(([k, v]) => (
                                <option key={k} value={k}>{v.label}</option>
                            ))}
                        </Select>
                    </Field>

                    <Field label="Supplier / provider">
                        <Input value={form.supplier} onChange={set("supplier")} placeholder="e.g. Yorkshire Scaffolds Ltd" testid="logistics-field-supplier" />
                    </Field>

                    <Field label="Description">
                        <Textarea value={form.description} onChange={set("description")} rows={2} placeholder="What's happening?" testid="logistics-field-description" />
                    </Field>

                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Expected date">
                            <Input type="date" value={form.expected_date} onChange={set("expected_date")} testid="logistics-field-date" />
                        </Field>
                        <Field label="Time window (optional)">
                            <Input value={form.expected_window} onChange={set("expected_window")} placeholder="08:00 - 12:00" testid="logistics-field-window" />
                        </Field>
                    </div>

                    <Field label="Destination">
                        <Select value={form.destination} onChange={set("destination")} testid="logistics-field-destination">
                            {Object.entries(DEST_META).map(([k, v]) => (
                                <option key={k} value={k}>{v.label}</option>
                            ))}
                        </Select>
                    </Field>

                    {form.destination === "other" && (
                        <Field label="Destination details">
                            <Input value={form.destination_details} onChange={set("destination_details")} placeholder="e.g. Subcontractor yard, LS2 4AB" testid="logistics-field-destination-details" />
                        </Field>
                    )}

                    <Field label="Status">
                        <Select value={form.status} onChange={set("status")} testid="logistics-field-status">
                            {STATUS_ORDER.map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
                        </Select>
                    </Field>

                    <label className="flex items-start gap-3 pt-1 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={form.customer_facing}
                            onChange={set("customer_facing")}
                            data-testid="logistics-field-customer-facing"
                            className="mt-0.5 w-4 h-4 rounded border-[#E5E7EB]"
                        />
                        <span className="text-sm text-[#131B2F]">
                            This affects the customer or their site
                            <span className="block text-xs text-[#4B5565] mt-0.5">
                                Used by later notification workflows. No live email/SMS is sent in this release.
                            </span>
                        </span>
                    </label>

                    <Field label="Notes">
                        <Textarea value={form.notes} onChange={set("notes")} rows={2} placeholder="Any internal notes for Operations" testid="logistics-field-notes" />
                    </Field>

                    {error && (
                        <div className="text-sm text-[#B91C1C] bg-[#FEF2F2] border border-[#FEE2E2] rounded-lg px-3 py-2" data-testid="logistics-dialog-error">
                            {error}
                        </div>
                    )}
                </div>

                <footer className="px-5 py-4 border-t border-[#E5E7EB] flex items-center justify-end gap-2">
                    <button type="button" onClick={onClose} className="h-9 px-4 rounded-full text-sm font-medium bg-white border border-[#E5E7EB] text-[#131B2F] hover:bg-[#F5F5F7]">
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={saving}
                        data-testid="logistics-dialog-save"
                        className="h-9 px-4 rounded-full text-sm font-medium bg-[#131B2F] text-white hover:brightness-110 disabled:opacity-60"
                    >
                        {saving ? "Saving…" : isEdit ? "Save changes" : "Add logistics"}
                    </button>
                </footer>
            </form>
        </div>
    );
}


// ─── Confirm delete ──────────────────────────────────────────────────────

function ConfirmDelete({ error, onCancel, onConfirm }) {
    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="logistics-delete-dialog">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-5">
                <div className="flex items-start gap-3 mb-3">
                    <div className="w-9 h-9 rounded-lg bg-[#FEF2F2] text-[#B91C1C] flex items-center justify-center">
                        <Ban size={16} strokeWidth={1.75} />
                    </div>
                    <div>
                        <div className="text-sm font-semibold text-[#131B2F]">Remove logistics item?</div>
                        <div className="text-xs text-[#4B5565] mt-0.5">This deletes the record. For a soft-close, set status to "Cancelled" instead.</div>
                    </div>
                </div>
                {error && (
                    <div className="text-xs text-[#B91C1C] bg-[#FEF2F2] border border-[#FEE2E2] rounded-md px-2 py-1.5 mb-3">{error}</div>
                )}
                <div className="flex items-center justify-end gap-2">
                    <button type="button" onClick={onCancel} className="h-9 px-4 rounded-full text-sm font-medium bg-white border border-[#E5E7EB] text-[#131B2F] hover:bg-[#F5F5F7]" data-testid="logistics-delete-cancel">
                        Cancel
                    </button>
                    <button type="button" onClick={onConfirm} className="h-9 px-4 rounded-full text-sm font-medium bg-[#B91C1C] text-white hover:brightness-110" data-testid="logistics-delete-confirm">
                        Remove
                    </button>
                </div>
            </div>
        </div>
    );
}


// ─── Form primitives ─────────────────────────────────────────────────────

const Field = ({ label, children }) => (
    <label className="block">
        <span className="block text-xs font-medium text-[#4B5565] mb-1">{label}</span>
        {children}
    </label>
);

const Input = ({ testid, ...p }) => (
    <input
        data-testid={testid}
        {...p}
        className="w-full h-10 px-3 rounded-lg border border-[#E5E7EB] bg-white text-sm text-[#131B2F] focus:border-[#131B2F] focus:outline-none"
    />
);

const Textarea = ({ testid, ...p }) => (
    <textarea
        data-testid={testid}
        {...p}
        className="w-full px-3 py-2 rounded-lg border border-[#E5E7EB] bg-white text-sm text-[#131B2F] focus:border-[#131B2F] focus:outline-none resize-none"
    />
);

const Select = ({ testid, children, ...p }) => (
    <select
        data-testid={testid}
        {...p}
        className="w-full h-10 px-3 rounded-lg border border-[#E5E7EB] bg-white text-sm text-[#131B2F] focus:border-[#131B2F] focus:outline-none"
    >
        {children}
    </select>
);
